import { useCallback, useEffect, useRef } from 'react';

/**
 * Admin listelerini arka planda güncel tutar: periyodik poll + sekme odağı.
 * @param {function(boolean): Promise<void>} fetchFn - silent=true ise loading göstermeden çalışır
 * @param {{ interval?: number, enabled?: boolean, deps?: unknown[] }} options
 */
export function useAdminAutoRefresh(fetchFn, options = {}) {
  const {
    interval = 5000,
    enabled = true,
    deps = [],
  } = options;

  const fetchRef = useRef(fetchFn);
  fetchRef.current = fetchFn;

  const run = useCallback(async (silent = false) => {
    await fetchRef.current(silent);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    run(false);

    let timerId = null;
    if (interval > 0) {
      timerId = setInterval(() => {
        if (document.hidden) return;
        run(true);
      }, interval);
    }

    const handleRefresh = () => {
      if (document.hidden) return;
      run(true);
    };

    window.addEventListener('focus', handleRefresh);
    document.addEventListener('visibilitychange', handleRefresh);

    return () => {
      if (timerId) clearInterval(timerId);
      window.removeEventListener('focus', handleRefresh);
      document.removeEventListener('visibilitychange', handleRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps controlled by caller
  }, [enabled, interval, run, ...deps]);

  return { refresh: () => run(false) };
}

export default useAdminAutoRefresh;
